import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { MODULES } from "@/config/modules";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function GlobalSearch() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get("/search", { params: { q } }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [q]);

  const total = (data?.groups || []).reduce((s, g) => s + g.count, 0);

  return (
    <div className="space-y-6">
      <div>
        <div className="overline text-muted-foreground mb-1">Global Search</div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Results for “{q}”</h1>
        <p className="text-sm text-muted-foreground mt-1">{loading ? "Searching…" : `${total} match${total !== 1 ? "es" : ""} across the CRM`}</p>
      </div>

      {!loading && total === 0 && (
        <Card className="p-12 text-center text-muted-foreground">
          <Icons.SearchX className="h-8 w-8 mx-auto opacity-40 mb-3" />
          No matches found. Try a different keyword.
        </Card>
      )}

      <div className="space-y-6">
        {(data?.groups || []).map((group) => {
          const module = MODULES[group.resource];
          const Icon = Icons[module.icon] || Icons.Circle;
          return (
            <div key={group.resource}>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-7 w-7 rounded-md flex items-center justify-center" style={{ backgroundColor: `${module.accent}1a`, color: module.accent }}>
                  <Icon className="h-4 w-4" />
                </div>
                <h3 className="font-display font-semibold">{module.name}</h3>
                <Badge variant="secondary">{group.count}</Badge>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {group.items.map((item, i) => (
                  <motion.div key={item.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                    onClick={() => navigate(module.customPath || `/m/${group.resource}`)} data-testid={`search-result-${item.id}`}
                    className="p-4 rounded-xl bg-card border border-border hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] duration-200 cursor-pointer">
                    <div className="font-medium truncate">{item[module.titleField] || "Untitled"}</div>
                    <div className="text-sm text-muted-foreground truncate">{item[module.subtitleField] || item.category || "—"}</div>
                    {(item.mobile || item.doc_number || item.status) && (
                      <div className="text-xs text-muted-foreground mt-2 flex flex-wrap gap-2">
                        {item.mobile && <span className="inline-flex items-center gap-1"><Icons.Phone className="h-3 w-3" />{item.mobile}</span>}
                        {item.doc_number && <span>#{item.doc_number}</span>}
                        {item.status && <Badge variant="secondary" className="text-[10px]">{item.status}</Badge>}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
